var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import Animate from 'rc-animate';
import omit from 'omit.js';
import classNames from 'classnames';
import ScrollNumber from './ScrollNumber';
import { PresetColorTypes } from '../_util/colors';
import { ConfigConsumer } from '../config-provider';
export { ScrollNumberProps } from './ScrollNumber';
function isPresetColor(color) {
    return PresetColorTypes.indexOf(color) !== -1;
}
const Badge = props => {
    const getNumberedDisplayCount = () => {
        const { count, overflowCount } = props;
        const displayCount = count > overflowCount ? `${overflowCount}+` : count;
        return displayCount;
    };
    const hasStatus = () => {
        const { status, color } = props;
        return !!status || !!color;
    };
    const isZero = () => {
        const numberedDisplayCount = getNumberedDisplayCount();
        return numberedDisplayCount === '0' || numberedDisplayCount === 0;
    };
    const isDot = () => {
        const { dot } = props;
        return (dot && !isZero()) || hasStatus();
    };
    const getDisplayCount = () => {
        // dot mode don't need count
        if (isDot()) {
            return '';
        }
        return getNumberedDisplayCount();
    };
    const getScrollNumberTitle = () => {
        const { title, count } = props;
        if (title) {
            return title;
        }
        return typeof count === 'string' || typeof count === 'number' ? count : undefined;
    };
    const getStyleWithOffset = () => {
        const { offset, style } = props;
        return offset
            ? Object.assign({ right: -parseInt(offset[0], 10), marginTop: offset[1] }, style) : style;
    };
    const getBadgeClassName = (prefixCls, direction = 'ltr') => {
        const { className, children } = props;
        return classNames(className, prefixCls, {
            [`${prefixCls}-status`]: hasStatus(),
            [`${prefixCls}-not-a-wrapper`]: !children,
            [`${prefixCls}-rtl`]: direction === 'rtl',
        });
    };
    const isHidden = () => {
        const { showZero } = props;
        const displayCount = getDisplayCount();
        const isEmpty = displayCount === null || displayCount === undefined || displayCount === '';
        return (isEmpty || (isZero() && !showZero)) && !isDot();
    };
    const renderStatusText = (prefixCls) => {
        const { text } = props;
        const hidden = isHidden();
        return hidden || !text ? null : <span className={`${prefixCls}-status-text`}>{text}</span>;
    };
    const renderDisplayComponent = () => {
        const { count } = props;
        const customNode = count;
        if (!customNode || typeof customNode !== 'object') {
            return undefined;
        }
        return React.cloneElement(customNode, {
            style: Object.assign(Object.assign({}, getStyleWithOffset()), (customNode.props && customNode.props.style)),
        });
    };
    const renderBadgeNumber = (prefixCls, scrollNumberPrefixCls) => {
        const { status, count, color } = props;
        const displayCount = getDisplayCount();
        const dot = isDot();
        const hidden = isHidden();
        const scrollNumberCls = classNames({
            [`${prefixCls}-dot`]: dot,
            [`${prefixCls}-count`]: !dot,
            [`${prefixCls}-multiple-words`]: !dot && count && count.toString && count.toString().length > 1,
            [`${prefixCls}-status-${status}`]: !!status,
            [`${prefixCls}-status-${color}`]: isPresetColor(color),
        });
        let statusStyle = getStyleWithOffset();
        if (color && !isPresetColor(color)) {
            statusStyle = statusStyle || {};
            statusStyle.background = color;
        }
        return hidden ? null : (<ScrollNumber prefixCls={scrollNumberPrefixCls} data-show={!hidden} className={scrollNumberCls} count={displayCount} displayComponent={renderDisplayComponent()} // <Badge status="success" count={<Icon type="xxx" />}></Badge>
         title={getScrollNumberTitle()} style={statusStyle} key="scrollNumber"/>);
    };
    const renderBadge = ({ getPrefixCls, direction }) => {
        const { prefixCls: customizePrefixCls, scrollNumberPrefixCls: customizeScrollNumberPrefixCls, children, status, text, color } = props, restProps = __rest(props, ["prefixCls", "scrollNumberPrefixCls", "children", "status", "text", "color"]);
        const omitArr = [
            'count',
            'showZero',
            'overflowCount',
            'className',
            'style',
            'dot',
            'offset',
            'title',
        ];
        const prefixCls = getPrefixCls('badge', customizePrefixCls);
        const scrollNumberPrefixCls = getPrefixCls('scroll-number', customizeScrollNumberPrefixCls);
        const scrollNumber = renderBadgeNumber(prefixCls, scrollNumberPrefixCls);
        const statusText = renderStatusText(prefixCls);
        const statusCls = classNames({
            [`${prefixCls}-status-dot`]: hasStatus(),
            [`${prefixCls}-status-${status}`]: !!status,
            [`${prefixCls}-status-${color}`]: isPresetColor(color),
        });
        const statusStyle = {};
        if (color && !isPresetColor(color)) {
            statusStyle.background = color;
        }
        // <Badge status="success" />
        if (!children && hasStatus()) {
            const styleWithOffset = getStyleWithOffset();
            const statusTextColor = styleWithOffset && styleWithOffset.color;
            return (<span {...omit(restProps, omitArr)} className={getBadgeClassName(prefixCls, direction)} style={styleWithOffset}>
          <span className={statusCls} style={statusStyle}/>
          <span style={{ color: statusTextColor }} className={`${prefixCls}-status-text`}>
            {text}
          </span>
        </span>);
        }
        return (<span {...omit(restProps, omitArr)} className={getBadgeClassName(prefixCls, direction)}>
        {children}
        <Animate component="" showProp="data-show" transitionName={children ? `${prefixCls}-zoom` : ''} transitionAppear>
          {scrollNumber}
        </Animate>
        {statusText}
      </span>);
    };
    return <ConfigConsumer>{renderBadge}</ConfigConsumer>;
};
Badge.defaultProps = {
    count: null,
    showZero: false,
    dot: false,
    overflowCount: 99,
};
export default Badge;
